import { createIdentity } from './identity.js';
import { isToolId, type ToolId } from './tool-registry.js';

export type HandoffRoute =
  | Readonly<{ targetTool: 'metadata' | 'pdf' | 'check'; action: 'inspect' }>
  | Readonly<{ targetTool: 'vector'; action: 'open' | 'trace' }>
  | Readonly<{ targetTool: 'images'; action: 'process' }>;
export type HandoffTarget = HandoffRoute['targetTool'];
export type HandoffSource = ToolId;
type Routed<T> = T & HandoffRoute;
export type FileProvenance = Readonly<{ kind: 'original' }> | Readonly<{
  kind: 'derived';
  parentFileIds: readonly string[];
  operation: 'check.prepare-export' | 'images.prepare-export' | 'vector.export';
}>;
export type FileHandoffRequest = Routed<Readonly<{
  file: File;
  sourceTool: HandoffSource;
  provenance: FileProvenance;
  signal?: AbortSignal;
}>>;
/** The name and MIME are hints. Receivers still inspect the actual file bytes. */
export type FileHandoff = Routed<Readonly<{
  id: string;
  fileId: string;
  file: File;
  name: string;
  mediaType: string;
  size: number;
  sourceTool: HandoffSource;
  provenance: FileProvenance;
}>>;
export type HandoffAdmission = Readonly<{ accepted: true }> | Readonly<{ accepted: false; reason: 'capacity' | 'unavailable' | 'invalid-input' | 'busy' | 'incompatible-mode' }>;
export type HandoffCancelReason = 'sender-cancelled' | 'superseded' | 'navigation' | 'released' | 'disposed';
export type HandoffReceipt = Readonly<{
  id: string;
  status: 'accepted' | 'rejected' | 'cancelled';
  reason?: HandoffCancelReason | 'capacity' | 'unavailable' | 'invalid-input' | 'receiver-error' | 'busy' | 'incompatible-mode';
}>;
export interface FileHandoffLease {
  readonly id: string;
  readonly pending: boolean;
  readonly outcome: Promise<HandoffReceipt>;
  /** Admission is synchronous; accepted means retained for inspection, not parsed or safe. */
  deliver(admit: (handoff: FileHandoff) => HandoffAdmission): HandoffReceipt;
  cancel(reason?: HandoffCancelReason): void;
  release(): void;
}

const fileIds = new WeakMap<File, string>();
const fileProvenance = new WeakMap<File, FileProvenance>();
const fileName = Object.getOwnPropertyDescriptor(File.prototype, 'name')!.get!;
const blobType = Object.getOwnPropertyDescriptor(Blob.prototype, 'type')!.get!;
const blobSize = Object.getOwnPropertyDescriptor(Blob.prototype, 'size')!.get!;

function checkedFile(file: File): { name: string; mediaType: string; size: number } {
  if (!(file instanceof File)) throw new TypeError('A handoff requires a File.');
  const name = fileName.call(file) as string, mediaType = blobType.call(file) as string, size = blobSize.call(file) as number;
  if (file.name !== name || file.type !== mediaType || file.size !== size) throw new TypeError('File properties must match the actual file.');
  return { name, mediaType, size };
}

/** Identity describes the File object, not its name, bytes, or a persisted document. */
export function fileIdentity(file: File): string {
  checkedFile(file);
  let id = fileIds.get(file);
  if (!id) { id = `file-${createIdentity().slice(4)}`; fileIds.set(file, id); }
  return id;
}

function objectKeys(value: unknown, permitted: readonly string[], label: string): asserts value is Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value) || Reflect.ownKeys(value).some(key => typeof key !== 'string' || !permitted.includes(key))) throw new TypeError(`Invalid ${label}.`);
}
function provenance(value: unknown): FileProvenance {
  objectKeys(value, ['kind', 'parentFileIds', 'operation'], 'file provenance');
  if (value.kind === 'original') {
    if (Reflect.ownKeys(value).some(key => key !== 'kind')) throw new TypeError('Original provenance cannot claim a transformation.');
    return Object.freeze({ kind: 'original' });
  }
  const operation = value.operation;
  const minimumParents = operation === 'vector.export' ? 0 : 1;
  if (value.kind !== 'derived' || typeof operation !== 'string' || !['check.prepare-export', 'images.prepare-export', 'vector.export'].includes(operation) || !Array.isArray(value.parentFileIds) || value.parentFileIds.length < minimumParents || value.parentFileIds.length > 50 || value.parentFileIds.some(id => typeof id !== 'string' || !/^file-[a-f0-9]{32}$/.test(id)) || new Set(value.parentFileIds).size !== value.parentFileIds.length) throw new TypeError('Derived provenance requires unique source file identities and a supported operation.');
  return Object.freeze({ kind: 'derived', operation: operation as 'check.prepare-export' | 'images.prepare-export' | 'vector.export', parentFileIds: Object.freeze([...value.parentFileIds]) as readonly string[] });
}

function sameProvenance(left: FileProvenance, right: FileProvenance): boolean {
  if (left.kind !== right.kind) return false;
  if (left.kind === 'original' || right.kind === 'original') return true;
  return left.operation === right.operation && left.parentFileIds.length === right.parentFileIds.length && left.parentFileIds.every((id, i) => id === right.parentFileIds[i]);
}

function route(value: unknown): asserts value is HandoffRoute {
  if (!value || typeof value !== 'object') throw new TypeError('Unsupported handoff tool or action.');
  const target = (value as { targetTool?: unknown }).targetTool, action = (value as { action?: unknown }).action;
  if (!((target === 'metadata' || target === 'pdf' || target === 'check') && action === 'inspect') && !(target === 'vector' && (action === 'open' || action === 'trace')) && !(target === 'images' && action === 'process')) throw new TypeError('Unsupported handoff tool or action.');
}

function envelope(request: FileHandoffRequest): FileHandoff {
  objectKeys(request, ['file', 'sourceTool', 'targetTool', 'action', 'provenance', 'signal'], 'handoff request');
  route(request);
  if (!isToolId(request.sourceTool)) throw new TypeError('Unsupported handoff source tool.');
  if (request.signal !== undefined && !(request.signal instanceof AbortSignal)) throw new TypeError('Invalid handoff cancellation signal.');
  const file = request.file, properties = checkedFile(file), source = provenance(request.provenance);
  const prior = fileProvenance.get(file);
  if (prior && !sameProvenance(prior, source)) throw new TypeError('A File cannot change its provenance between handoffs.');
  if (!prior && source.kind === 'derived') {
    const owner = source.operation === 'check.prepare-export' ? 'check' : source.operation === 'images.prepare-export' ? 'images' : 'vector';
    if (request.sourceTool !== owner) throw new TypeError(`Prepared exports belong to ${owner === 'check' ? 'Check' : owner === 'images' ? 'Images' : 'Vector'}.`);
  }
  const fileId = fileIdentity(file);
  if (source.kind === 'derived' && source.parentFileIds.includes(fileId)) throw new TypeError('A derived file cannot be its own source.');
  const result = Object.freeze({ id: `handoff-${createIdentity().slice(4)}`, fileId, file, ...properties, sourceTool: request.sourceTool, targetTool: request.targetTool, action: request.action, provenance: source }) as FileHandoff;
  if (!prior) fileProvenance.set(file, source);
  return result;
}

type PendingHandoff = {
  id: string;
  envelope?: FileHandoff;
  receipt?: HandoffReceipt;
  admitting: boolean;
  resolve: (receipt: HandoffReceipt) => void;
  removeAbort?: () => void;
};

/** One temporary shell-owned transfer. No file registry, object URLs, or persistence. */
export class FileHandoffBroker {
  private current?: PendingHandoff;
  private disposed = false;
  get hasPending(): boolean { return !!this.current; }

  begin(request: FileHandoffRequest): FileHandoffLease {
    if (this.disposed) throw new Error('The handoff broker has been disposed.');
    // Invalid requests do not cancel a valid transfer already waiting for its route.
    const handoff = envelope(request), signal = request.signal;
    this.cancelPending('superseded');
    let resolve!: (receipt: HandoffReceipt) => void;
    const outcome = new Promise<HandoffReceipt>(done => { resolve = done; });
    const pending: PendingHandoff = { id: handoff.id, envelope: handoff, admitting: false, resolve };
    this.current = pending;
    const cancel = (reason: HandoffCancelReason = 'sender-cancelled') => {
      if (!pending.receipt && !pending.admitting) this.settle(pending, { id: pending.id, status: 'cancelled', reason });
    };
    if (signal) {
      const abort = () => cancel('sender-cancelled');
      signal.addEventListener('abort', abort, { once: true });
      pending.removeAbort = () => signal.removeEventListener('abort', abort);
      if (signal.aborted) abort();
    }
    return Object.freeze({
      id: pending.id,
      get pending() { return !!pending.envelope && !pending.admitting; },
      outcome,
      deliver: (admit: (handoff: FileHandoff) => HandoffAdmission) => this.deliver(pending, admit),
      cancel,
      release: () => cancel('released'),
    });
  }

  cancelPending(reason: HandoffCancelReason = 'navigation'): void {
    const pending = this.current;
    if (pending) this.settle(pending, { id: pending.id, status: 'cancelled', reason });
  }
  dispose(): void { this.disposed = true; this.cancelPending('disposed'); }

  private settle(pending: PendingHandoff, value: HandoffReceipt): HandoffReceipt {
    if (pending.receipt) return pending.receipt;
    pending.removeAbort?.(); pending.removeAbort = undefined;
    pending.envelope = undefined;
    if (this.current === pending) this.current = undefined;
    pending.receipt = Object.freeze(value);
    pending.resolve(pending.receipt);
    return pending.receipt;
  }

  private deliver(pending: PendingHandoff, admit: (handoff: FileHandoff) => HandoffAdmission): HandoffReceipt {
    if (pending.receipt) return pending.receipt;
    if (pending.admitting) throw new Error('This handoff is already being admitted.');
    const handoff = pending.envelope;
    if (!handoff || this.current !== pending || this.disposed) return this.settle(pending, { id: pending.id, status: 'cancelled', reason: 'released' });
    // The callback is the ownership boundary. Pending cancellation stops here;
    // a retained File cannot be revoked after synchronous receiver admission.
    pending.admitting = true;
    pending.removeAbort?.(); pending.removeAbort = undefined;
    this.current = undefined;
    try {
      const result = admit(handoff);
      if (result instanceof Promise) {
        // A miswired async receiver violates this interface; do not also leak
        // an unhandled rejection after reporting failed synchronous admission.
        void result.catch(() => {});
        return this.settle(pending, { id: pending.id, status: 'rejected', reason: 'receiver-error' });
      }
      if (result?.accepted === true) return this.settle(pending, { id: pending.id, status: 'accepted' });
      if (result?.accepted === false && ['capacity', 'unavailable', 'invalid-input', 'busy', 'incompatible-mode'].includes(result.reason)) return this.settle(pending, { id: pending.id, status: 'rejected', reason: result.reason });
      return this.settle(pending, { id: pending.id, status: 'rejected', reason: 'receiver-error' });
    } catch {
      return this.settle(pending, { id: pending.id, status: 'rejected', reason: 'receiver-error' });
    }
  }
}
